import { useEffect } from "react";
import { useNavigate } from "react-router";

import {
  ClientButton,
  ClientInput,
  ClientSectorSelect,
  ClientViewModeToggle,
  SelectionCardGroup,
} from "@/components/clients";
import { clientPath, useClientBasePath } from "@/hooks/use-client-base-path";
import { useClientViewMode } from "@/hooks/use-client-view-mode";
import { useTciSession } from "@/hooks/use-tci-session";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import { isRegionId, listRegions, toRegionalSpelling } from "@/lib/tci";

/**
 * First client screen (Figma 654:259): region, sector, organisation name.
 * Presenter/Visitor toggle only on `/presenter`.
 */
export function ClientIntroPage() {
  const navigate = useNavigate();
  const basePath = useClientBasePath();
  const { session, updateSession } = useTciSession();
  const { track } = useVisitorTracking();
  const [viewMode, setViewMode] = useClientViewMode();
  const showViewModeToggle = basePath === "/presenter";

  useEffect(() => {
    track({ currentStep: "intro", lastEvent: "view_intro" });
  }, [track]);

  const regionOptions = listRegions().map((region) => ({
    id: region.id,
    title: region.title,
    description: region.description,
  }));

  const regionId = session?.regionId ?? null;
  const businessSector = session?.businessSector ?? null;
  const organisationName = session?.organisationName ?? "";
  const organisationLabel = toRegionalSpelling("Organization Name", regionId);
  const canContinue = Boolean(
    regionId && businessSector && organisationName.trim(),
  );

  function handleRegionChange(id: string) {
    if (!isRegionId(id)) return;
    updateSession({ regionId: id });
  }

  function handleContinue() {
    if (!canContinue || !regionId || !businessSector) return;
    track({
      currentStep: "intro",
      lastEvent: "intro_continue",
      regionId,
      businessSector,
      organisationName: organisationName.trim(),
    });
    navigate(clientPath(basePath, "workflow"));
  }

  return (
    <section className="my-auto flex w-full flex-col gap-6">
      <div className="flex w-full flex-col gap-10 sm:gap-14 lg:gap-[60px]">
        <div
          className={
            showViewModeToggle
              ? "flex w-full items-start justify-between gap-4"
              : "flex w-full items-start"
          }
        >
          <h1 className="w-full max-w-[507px] font-['Inter'] text-[28px] font-normal leading-9 tracking-[-1.2px] text-white sm:text-4xl sm:leading-[44px] sm:tracking-[-1.8px] lg:text-[48.4px] lg:leading-[56.4px] lg:tracking-[-2.82px]">
            Find Out How Much Profit Is Trapped In Your{" "}
            <span className="text-[#38bdf8]">Daily Operations</span>
          </h1>

          {showViewModeToggle ? (
            <ClientViewModeToggle
              className="shrink-0"
              value={viewMode}
              onChange={setViewMode}
            />
          ) : null}
        </div>

        <div className="flex w-full flex-col items-start gap-4">
          <p className="font-['Inter'] text-base font-normal leading-9 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]">
            What region is your business based?
          </p>

          <SelectionCardGroup
            aria-label="Business region"
            className="w-full sm:flex-nowrap"
            options={regionOptions}
            value={regionId ?? undefined}
            onChange={handleRegionChange}
          />
        </div>
      </div>

      <div className="flex w-full flex-col items-end gap-10">
        <div className="flex w-full flex-col gap-6">
          <div className="flex w-full flex-col gap-2">
            <p className="font-['Inter'] text-base font-normal leading-9 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]">
              Business Sector
            </p>
            <ClientSectorSelect
              value={businessSector}
              onChange={(sector) => updateSession({ businessSector: sector })}
            />
          </div>

          <div className="flex w-full flex-col gap-2">
            <p className="font-['Inter'] text-base font-normal leading-9 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]">
              {organisationLabel}
            </p>
            <ClientInput
              variant="text"
              placeholder="e.g. Enbros"
              value={organisationName}
              onChange={(e) =>
                updateSession({ organisationName: e.target.value })
              }
              aria-label={organisationLabel}
            />
          </div>
        </div>

        <div className="flex w-full max-w-[539px] items-center justify-end self-stretch sm:self-end">
          <ClientButton
            variant="primary"
            type="button"
            disabled={!canContinue}
            onClick={handleContinue}
          >
            Continue
          </ClientButton>
        </div>
      </div>
    </section>
  );
}
